import { ErrorTimeout } from 'oidc-client-ts';

/**
 * Why a session could not be renewed. `offline`: Keycloak was not reached (no network yet after
 * the computer woke up, Keycloak restarting); the session may still be valid, so the page stays
 * and the next request tries again. `ended`: Keycloak refused the refresh token (the 8-hour
 * session is over, signed out elsewhere) or there is none; only a new sign-in helps.
 */
export type RenewalFailure = 'offline' | 'ended';

export function renewalFailure(error: unknown, online = isOnline()): RenewalFailure {
  if (!online || error instanceof ErrorTimeout) return 'offline';
  // fetch rejects with a TypeError when the server cannot be reached
  if (error instanceof TypeError) return 'offline';
  return 'ended';
}

function isOnline(): boolean {
  return typeof navigator === 'undefined' || navigator.onLine !== false;
}

/** What the sign-in request carries back to the application (OpenID Connect `state`). */
export interface SignInState {
  returnTo: string;
}

/**
 * The page to open after signing in: the one the user was on, when it is a path of this
 * application. Anything else (another site, the callback itself) gives the dashboard.
 */
export function returnPath(state: unknown): string {
  const target = (state as Partial<SignInState> | null | undefined)?.returnTo;
  if (typeof target !== 'string' || !target.startsWith('/')) return '/';
  if (target.startsWith('//') || target.startsWith('/\\')) return '/';
  if (target.startsWith('/auth/callback')) return '/';
  return target;
}
